import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  getAdminSupportSummary,
  getAdminSupportTickets,
  getAdminSupportTicket,
  patchAdminSupportTicket,
  addAdminSupportTicketMessage,
  getApiError,
} from '../../lib/api';
import { toast } from 'sonner';
import { cn, formatDateTime } from '../../lib/utils';
import { BarChart3, ClipboardList, LifeBuoy } from 'lucide-react';

const STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'waiting_customer', label: 'Waiting on customer' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'resolved', label: 'Resolved' },
];

const STATUS_LABELS = Object.fromEntries(STATUSES.map((s) => [s.value, s.label]));

function statusBadgeVariant(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'resolved') return 'secondary';
  if (s === 'waiting_customer') return 'outline';
  if (s === 'on_hold') return 'outline';
  return 'default';
}

export function AdminSupportLayout() {
  return (
    <div className="space-y-6" data-testid="admin-support-layout">
      <div>
        <h1 className="text-2xl sm:text-3xl font-jakarta font-bold text-slate-900 flex items-center gap-2">
          <LifeBuoy className="w-7 h-7 text-teal-600" />
          Support
        </h1>
        <p className="text-slate-500 mt-1">Tickets, replies, and reporting.</p>
      </div>
      <nav className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        <NavLink
          to="/admin/support"
          end
          className={({ isActive }) =>
            cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              isActive ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-100'
            )
          }
        >
          <span className="inline-flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Dashboard
          </span>
        </NavLink>
        <NavLink
          to="/admin/support/tickets"
          className={({ isActive }) =>
            cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              isActive ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-100'
            )
          }
        >
          <span className="inline-flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />
            All tickets
          </span>
        </NavLink>
      </nav>
      <Outlet />
    </div>
  );
}

export function AdminSupportDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await getAdminSupportSummary();
        setSummary(res?.data || null);
      } catch (e) {
        toast.error(getApiError(e).uiMessage || getApiError(e).detail || 'Could not load summary');
        setSummary(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const byStatus = summary?.by_status || summary?.counts || {};
  const recent = Array.isArray(summary?.recent_tickets) ? summary.recent_tickets : [];

  return (
    <div className="space-y-6" data-testid="admin-support-dashboard">
      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {STATUSES.map((s) => (
              <Card key={s.value} className="border-slate-200">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">{s.label}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{Number(byStatus[s.value] ?? 0).toLocaleString()}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          {summary?.avg_first_response_hours != null && (
            <p className="text-sm text-slate-600">
              Avg. first response (hours):{' '}
              <span className="font-semibold">{Number(summary.avg_first_response_hours).toFixed(1)}</span>
            </p>
          )}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Recent tickets</CardTitle>
            </CardHeader>
            <CardContent>
              {recent.length === 0 ? (
                <p className="text-sm text-slate-500">No recent tickets.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {recent.map((t) => {
                    const id = t.ticket_id || t.id;
                    return (
                      <li key={id} className="py-2">
                        <Link to={`/admin/support/tickets/${encodeURIComponent(id)}`} className="text-teal-700 hover:underline font-medium">
                          {t.title || id}
                        </Link>
                        <p className="text-xs text-slate-500">
                          {id} · {STATUS_LABELS[String(t.status || '').toLowerCase()] || t.status}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

const CONTENT_MAX = 8000;

export function AdminSupportTicketsListPage() {
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const res = await getAdminSupportTickets({
        search: search.trim() || undefined,
        status: statusFilter || undefined,
        limit: 100,
        skip: 0,
      });
      const list = Array.isArray(res?.data?.tickets) ? res.data.tickets : res?.data?.items || [];
      setTickets(list);
    } catch (e) {
      toast.error(getApiError(e).uiMessage || getApiError(e).detail || 'Could not load tickets');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4" data-testid="admin-support-tickets-list">
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Search tickets</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row gap-3 flex-wrap">
          <Input
            placeholder="Title, ticket ID, customer email, agent ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-md"
          />
          <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button type="button" variant="secondary" onClick={load}>
            Search / refresh
          </Button>
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : tickets.length === 0 ? (
        <p className="text-sm text-slate-500">No tickets match.</p>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <ul className="divide-y divide-slate-100">
              {tickets.map((t) => {
                const id = t.ticket_id || t.id;
                return (
                  <li key={id}>
                    <Link
                      to={`/admin/support/tickets/${encodeURIComponent(id)}`}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 hover:bg-slate-50"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">{t.title || 'Untitled'}</p>
                        <p className="text-xs text-slate-500 truncate">
                          {id} · {t.customer_email || t.business_name || '—'} · Agent {t.agent_id || '—'}
                        </p>
                      </div>
                      <Badge variant={statusBadgeVariant(t.status)} className="shrink-0 w-fit">
                        {STATUS_LABELS[String(t.status || '').toLowerCase()] || t.status}
                      </Badge>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function AdminSupportTicketDetailPage() {
  const { ticketId } = useParams();
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getAdminSupportTicket(ticketId);
      setTicket(res?.data?.ticket || res?.data || null);
    } catch (e) {
      toast.error(getApiError(e).uiMessage || getApiError(e).detail || 'Could not load ticket');
      setTicket(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [ticketId]);

  const handleStatus = async (next) => {
    setStatusSaving(true);
    try {
      await patchAdminSupportTicket(ticketId, { status: next });
      toast.success('Status updated');
      await load();
    } catch (e) {
      toast.error(getApiError(e).uiMessage || getApiError(e).detail || 'Could not update status');
    } finally {
      setStatusSaving(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await addAdminSupportTicketMessage(ticketId, { content: reply.trim().slice(0, CONTENT_MAX) });
      setReply('');
      toast.success('Reply sent');
      await load();
    } catch (e) {
      toast.error(getApiError(e).uiMessage || getApiError(e).detail || 'Could not send reply');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 max-w-4xl">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div>
        <Button variant="outline" asChild>
          <Link to="/admin/support/tickets">Back to tickets</Link>
        </Button>
        <p className="text-slate-600 mt-4">Ticket not found.</p>
      </div>
    );
  }

  const messages = Array.isArray(ticket.messages) ? ticket.messages : [];
  const sorted = [...messages].sort(
    (a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()
  );
  const currentStatus = String(ticket.status || '').toLowerCase();

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-support-ticket-detail">
      <Button variant="outline" asChild>
        <Link to="/admin/support/tickets">← All tickets</Link>
      </Button>

      <Card className="border-slate-200">
        <CardHeader className="space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="min-w-0">
              <CardTitle className="text-xl leading-tight">{ticket.title || 'Support ticket'}</CardTitle>
              <p className="text-sm text-slate-500 mt-2 font-mono text-xs">
                {ticket.ticket_id || ticketId}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              <Select
                value={currentStatus}
                onValueChange={handleStatus}
                disabled={statusSaving}
              >
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-lg border border-slate-100 bg-slate-50 p-4 grid sm:grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-slate-500 uppercase">Customer</p>
              <p className="font-medium text-slate-900">{ticket.customer_name || '—'}</p>
              <p className="text-slate-600 break-all">{ticket.customer_email || '—'}</p>
              <p className="text-xs text-slate-500 mt-1 font-mono">{ticket.customer_id || ticket.user_id || ''}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Business</p>
              <p className="font-medium text-slate-900">{ticket.business_name || ticket.company_name || '—'}</p>
              <p className="text-xs text-slate-500 mt-1 font-mono">{ticket.business_id || ''}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Agent</p>
              <p className="font-medium text-slate-900">{ticket.agent_name || '—'}</p>
              <p className="font-mono text-xs text-slate-600">{ticket.agent_id || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Timestamps</p>
              <p className="text-slate-700">Created {formatDateTime(ticket.created_at)}</p>
              <p className="text-slate-700">Updated {formatDateTime(ticket.updated_at)}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-lg border border-slate-100 p-4 space-y-2">
            <p className="text-xs font-medium uppercase text-slate-500">Initial request</p>
            <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">{ticket.content || ticket.body}</p>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-semibold text-slate-800">Thread</p>
            {sorted.length === 0 ? (
              <p className="text-sm text-slate-500">No replies yet.</p>
            ) : (
              <ul className="space-y-3">
                {sorted.map((m) => {
                  const mid = m.message_id || m.id;
                  const from = String(m.author_role || m.role || '').toLowerCase();
                  const isStaff = from === 'admin' || from === 'staff' || from === 'agent';
                  return (
                    <li
                      key={mid || `${m.created_at}`}
                      className={cn(
                        'rounded-lg border p-3 text-sm',
                        isStaff ? 'border-teal-100 bg-teal-50/40' : 'border-slate-100 bg-white'
                      )}
                    >
                      <div className="flex justify-between gap-2 text-xs text-slate-500 mb-1">
                        <span className="font-medium text-slate-700">
                          {isStaff ? `Staff${m.author_name ? ` (${m.author_name})` : ''}` : 'Customer'}
                        </span>
                        <span>{formatDateTime(m.created_at)}</span>
                      </div>
                      <p className="text-slate-800 whitespace-pre-wrap break-words">{m.content || m.body}</p>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <form onSubmit={handleReply} className="space-y-2 border-t border-slate-100 pt-4">
            <label className="text-sm font-medium text-slate-700">Reply (plain text)</label>
            <Textarea
              value={reply}
              onChange={(e) => setReply(e.target.value.slice(0, CONTENT_MAX))}
              rows={4}
              placeholder="Your reply to the customer"
              maxLength={CONTENT_MAX}
            />
            <Button type="submit" disabled={sending || !reply.trim()} className="bg-teal-600 hover:bg-teal-700">
              {sending ? 'Sending…' : 'Send reply'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
